import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
  Badge,
  Divider,
  SimpleGrid,
} from '@chakra-ui/react'
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react'
import { Link as RouterLink } from 'react-router-dom'
import { apiClient } from '@/lib/api/client'
import { checklistTemplateService, type ChecklistTemplate } from '@/lib/services/checklistTemplateService'
import { taskFieldModuleService } from '@/lib/services/taskFieldModuleService'
import { taskTemplateService, type TaskTemplateInput, type TaskTemplateStepInput } from '@/lib/services/taskTemplateService'
import type { TaskFormFieldDefinition } from '@/components/helpdesk/TicketDetail/tasks/modules/types'
import { useToastNotification } from '@/lib/utils/useToastNotification'
type StatusOption = { label: string; value: string; scope?: string | null }

function ruleToInput(value: unknown): string {
  if (value == null || value === '') return ''
  if (typeof value === 'string') return value
  return JSON.stringify(value, null, 2)
}

function parseRuleInput(value: string): Record<string, unknown> | string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  try {
    const parsed = JSON.parse(trimmed) as unknown
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
  } catch {
    return trimmed
  }
  return trimmed
}

const FIELD_BUNDLES: Record<string, TaskFormFieldDefinition[]> = {
  fe: [
    { key: 'store', module: 'core.store', label: 'Store', required: true },
    { key: 'fe', module: 'core.fe_schedule', label: 'FE schedule', requires: ['store'] },
  ],
  permit: [{ key: 'permit', module: 'core.work_permit', label: 'Work permit' }],
  parts: [{ key: 'parts', module: 'core.parts', label: 'Parts' }],
  assets: [{ key: 'assets', module: 'core.assets', label: 'Equipment' }],
}

const MODULE_FIELD_DEFAULTS: Partial<Record<string, Partial<TaskFormFieldDefinition>>> = {
  'core.store': { required: true },
  'core.fe_schedule': { requires: ['store'] },
}

type Props = {
  templateId?: number
  onSuccess: () => void
  onCancel: () => void
}

export function TaskTemplateForm({ templateId, onSuccess, onCancel }: Props) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(Boolean(templateId))
  const [saving, setSaving] = useState(false)
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([])
  const [supportTypes, setSupportTypes] = useState<Array<{ id: number; label: string }>>([])
  const [modulePalette, setModulePalette] = useState<
    Array<{ module_key: string; label: string; category: string; default_config?: Record<string, unknown> | null }>
  >([])

  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [description, setDescription] = useState('')
  const [titlePattern, setTitlePattern] = useState('')
  const [notesPattern, setNotesPattern] = useState('')
  const [defaultAssigneeRule, setDefaultAssigneeRule] = useState('')
  const [defaultPriorityRule, setDefaultPriorityRule] = useState('')
  const [dueDateRule, setDueDateRule] = useState('')
  const [allowedCreatorRoles, setAllowedCreatorRoles] = useState('')
  const [taskFieldModuleCode, setTaskFieldModuleCode] = useState('')
  const [defaultStatusCode, setDefaultStatusCode] = useState('')
  const [supportTypeId, setSupportTypeId] = useState<string>('')
  const [isSequential, setIsSequential] = useState(true)
  const [isActive, setIsActive] = useState(true)
  const [steps, setSteps] = useState<TaskTemplateStepInput[]>([])
  const [checklistTemplateId, setChecklistTemplateId] = useState<string>('')
  const [checklistLibrary, setChecklistLibrary] = useState<ChecklistTemplate[]>([])

  useEffect(() => {
    void taskFieldModuleService.listHelpdesk().then((list) => {
      setModulePalette(
        list.map((m) => ({
          module_key: m.module_key,
          label: m.label,
          category: m.category,
          default_config: m.default_config ?? null,
        }))
      )
    })
    apiClient
      .get<{ data: Array<{ id: number; code: string; label?: string | null }> }>('/service-catalog/support-types', {
        params: { per_page: 100 },
      })
      .then((res) => {
        const items = res.data?.data
        if (Array.isArray(items)) {
          setSupportTypes(items.map((s) => ({ id: s.id, label: s.label || s.code })))
        }
      })
      .catch(() => {})
    apiClient
      .get<{
        data: Array<{
          code: string
          label?: string | null
          scope?: string | null
          sort_order?: number | null
        }>
      }>('/service-catalog/child-ticket-statuses', {
        params: { per_page: 100 },
      })
      .then((res) => {
        const items = res.data?.data
        if (Array.isArray(items)) {
          const sorted = [...items].sort((a, b) => {
            const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0)
            if (orderDiff !== 0) return orderDiff
            return (a.label || a.code).localeCompare(b.label || b.code)
          })
          setStatusOptions(
            sorted.map((s) => ({
              label: s.label || s.code,
              value: s.code,
              scope: s.scope ?? null,
            }))
          )
        }
      })
      .catch(() => {})
    void checklistTemplateService
      .list({ per_page: 200, active: true })
      .then(({ items }) => setChecklistLibrary(items))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!templateId) return
    setLoading(true)
    taskTemplateService
      .get(templateId)
      .then((tpl) => {
        setName(tpl.name)
        setCode(tpl.code ?? '')
        setDescription(tpl.description ?? '')
        setTitlePattern(tpl.title_pattern ?? '')
        setNotesPattern(tpl.notes_pattern ?? '')
        setDefaultAssigneeRule(ruleToInput(tpl.default_assignee_rule))
        setDefaultPriorityRule(ruleToInput(tpl.default_priority_rule))
        setDueDateRule(ruleToInput(tpl.due_date_rule))
        setAllowedCreatorRoles((tpl.allowed_creator_roles ?? []).join(', '))
        setTaskFieldModuleCode(tpl.task_field_module_code ?? '')
        setDefaultStatusCode(tpl.default_status_code ?? '')
        setSupportTypeId(tpl.support_type_id != null ? String(tpl.support_type_id) : '')
        setIsSequential(tpl.is_sequential ?? true)
        setIsActive(tpl.is_active ?? true)
        setSteps(
          (tpl.steps ?? []).map((s) => ({
            id: s.id,
            temp_id: `step_${s.id}`,
            title: s.title,
            status_code: s.status_code,
            form_fields: (s.form_fields as TaskFormFieldDefinition[]) ?? [],
            default_sla_minutes: s.default_sla_minutes ?? null,
            sort_order: s.sort_order,
            depends_on_step_id: s.depends_on_step_id ?? null,
            checklist_template_id: s.checklist_template_id ?? null,
          }))
        )
        setChecklistTemplateId(tpl.checklist_template_id != null ? String(tpl.checklist_template_id) : '')
      })
      .catch((e) => showError(e as Error))
      .finally(() => setLoading(false))
  }, [templateId, showError])

  const groupedModules = useMemo(() => {
    const groups: Record<string, typeof modulePalette> = {}
    for (const m of modulePalette) {
      groups[m.category] = groups[m.category] ?? []
      groups[m.category].push(m)
    }
    return groups
  }, [modulePalette])

  const inferStepScope = useCallback((step: TaskTemplateStepInput): 'parts' | 'fe_visit' | 'permit' | null => {
    const modules = (step.form_fields ?? []).map((f) => f.module)
    if (modules.includes('core.parts')) return 'parts'
    if (modules.includes('core.fe_schedule') || modules.includes('core.store')) return 'fe_visit'
    if (modules.includes('core.work_permit')) return 'permit'
    return null
  }, [])

  const getFilteredStatusOptions = useCallback((step: TaskTemplateStepInput): StatusOption[] => {
    const scope = inferStepScope(step)
    if (!scope) return statusOptions
    return statusOptions.filter((status) => status.scope === 'generic' || status.scope === scope)
  }, [inferStepScope, statusOptions])

  const addStep = () => {
    setSteps((prev) => [
      ...prev,
      {
        temp_id: `step_${Date.now()}`,
        title: '',
        status_code: statusOptions[0]?.value ?? 'open',
        form_fields: [],
        default_sla_minutes: null,
        sort_order: prev.length + 1,
      },
    ])
  }

  const updateStep = (index: number, patch: Partial<TaskTemplateStepInput>) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)))
  }

  const removeStep = (index: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, sort_order: i + 1 })))
  }

  const moveStep = (index: number, dir: -1 | 1) => {
    setSteps((prev) => {
      const next = [...prev]
      const target = index + dir
      if (target < 0 || target >= next.length) return prev
      ;[next[index], next[target]] = [next[target], next[index]]
      return next.map((s, i) => ({ ...s, sort_order: i + 1 }))
    })
  }

  const addFieldToStep = (stepIndex: number, moduleKey: string, label: string) => {
    const key = moduleKey.replace(/\./g, '_') + '_' + Date.now()
    const defaults = MODULE_FIELD_DEFAULTS[moduleKey] ?? {}
    const paletteEntry = modulePalette.find((m) => m.module_key === moduleKey)
    const config =
      paletteEntry?.default_config && typeof paletteEntry.default_config === 'object'
        ? { ...paletteEntry.default_config }
        : {}
    updateStep(stepIndex, {
      form_fields: [
        ...(steps[stepIndex]?.form_fields ?? []),
        { key, module: moduleKey, label, required: false, config, ...defaults },
      ],
    })
  }

  const addBundle = (stepIndex: number, bundleKey: keyof typeof FIELD_BUNDLES) => {
    const bundle = FIELD_BUNDLES[bundleKey]
    const ts = Date.now()
    const storeKey = `store_${ts}`
    const fields = bundle.map((f, i) => {
      const key =
        f.module === 'core.store' ? storeKey : f.module === 'core.fe_schedule' ? `fe_${ts}` : `${f.key}_${ts}_${i}`
      const requires = f.requires?.map((r) => (r === 'store' ? storeKey : r))
      return { ...f, key, requires }
    })
    updateStep(stepIndex, {
      form_fields: [...(steps[stepIndex]?.form_fields ?? []), ...fields],
    })
  }

  const handleSubmit = async () => {
    if (!name.trim()) {
      showError(new Error('Name is required'))
      return
    }
    setSaving(true)
    try {
      const payload: TaskTemplateInput = {
        name: name.trim(),
        code: code.trim() || null,
        description: description.trim() || null,
        title_pattern: titlePattern.trim() || null,
        notes_pattern: notesPattern.trim() || null,
        default_assignee_rule: parseRuleInput(defaultAssigneeRule),
        default_priority_rule: parseRuleInput(defaultPriorityRule),
        due_date_rule: parseRuleInput(dueDateRule),
        allowed_creator_roles: allowedCreatorRoles
          .split(',')
          .map((role) => role.trim())
          .filter(Boolean),
        task_field_module_code: taskFieldModuleCode.trim() || null,
        default_status_code: defaultStatusCode.trim() || null,
        support_type_id: supportTypeId ? Number(supportTypeId) : null,
        is_sequential: isSequential,
        is_active: isActive,
        checklist_template_id: checklistTemplateId ? Number(checklistTemplateId) : null,
        steps,
      }
      if (templateId) {
        await taskTemplateService.update(templateId, payload)
        showSuccess('Template updated')
      } else {
        await taskTemplateService.create(payload)
        showSuccess('Template created')
      }
      onSuccess()
    } catch (e) {
      showError(e as Error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <Text>Loading template…</Text>
  }

  return (
    <VStack align="stretch" spacing={5}>
      <FormControl isRequired>
        <FormLabel>Name</FormLabel>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </FormControl>
      <FormControl>
        <FormLabel>Code</FormLabel>
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="e.g. parts_procurement"
        />
        <Text fontSize="xs" color="gray.500" mt={1}>
          Stable identifier used when creating tasks via template_code.
        </Text>
      </FormControl>
      <FormControl>
        <FormLabel>Description</FormLabel>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
      </FormControl>

      <Box p={4} borderWidth="1px" borderRadius="md" bg="white">
        <Text fontWeight="700" mb={3}>
          Resolution defaults
        </Text>
        <VStack align="stretch" spacing={3}>
          <FormControl>
            <FormLabel>Title pattern</FormLabel>
            <Input
              value={titlePattern}
              onChange={(e) => setTitlePattern(e.target.value)}
              placeholder="Parts procurement — {parent.ticket_number}"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Notes pattern</FormLabel>
            <Textarea
              value={notesPattern}
              onChange={(e) => setNotesPattern(e.target.value)}
              rows={2}
              placeholder="Purpose: {context.document_purpose}"
            />
          </FormControl>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            <FormControl>
              <FormLabel>Default assignee rule</FormLabel>
              <Textarea
                value={defaultAssigneeRule}
                onChange={(e) => setDefaultAssigneeRule(e.target.value)}
                rows={3}
                fontFamily="mono"
                fontSize="sm"
                placeholder='{"type":"parent_assigned_to"}'
              />
            </FormControl>
            <FormControl>
              <FormLabel>Default priority rule</FormLabel>
              <Textarea
                value={defaultPriorityRule}
                onChange={(e) => setDefaultPriorityRule(e.target.value)}
                rows={3}
                fontFamily="mono"
                fontSize="sm"
                placeholder='{"type":"inherit_parent_severity"}'
              />
            </FormControl>
          </SimpleGrid>
          <FormControl>
            <FormLabel>Due date rule</FormLabel>
            <Textarea
              value={dueDateRule}
              onChange={(e) => setDueDateRule(e.target.value)}
              rows={3}
              fontFamily="mono"
              fontSize="sm"
              placeholder='{"type":"business_days_offset","days":5}'
            />
          </FormControl>
          <FormControl>
            <FormLabel>Allowed creator roles</FormLabel>
            <Input
              value={allowedCreatorRoles}
              onChange={(e) => setAllowedCreatorRoles(e.target.value)}
              placeholder="Role name or id, comma-separated"
            />
          </FormControl>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            <FormControl>
              <FormLabel>Task field module</FormLabel>
              <Select
                value={taskFieldModuleCode}
                onChange={(e) => setTaskFieldModuleCode(e.target.value)}
                placeholder="Optional module for standalone tasks"
              >
                {modulePalette.map((module) => (
                  <option key={module.module_key} value={module.module_key}>
                    {module.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Default status</FormLabel>
              <Select
                value={defaultStatusCode}
                onChange={(e) => setDefaultStatusCode(e.target.value)}
                placeholder="Optional initial status"
              >
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </SimpleGrid>
          <Text fontSize="xs" color="gray.500">
            Placeholders: {'{parent.ticket_number}'}, {'{parent.subject}'}, {'{context.document_purpose}'},{' '}
            {'{context.part_names}'}.
          </Text>
        </VStack>
      </Box>

      <HStack align="flex-start" spacing={4} flexWrap="wrap">
        <FormControl maxW="280px">
          <FormLabel>Support type</FormLabel>
          <Select value={supportTypeId} onChange={(e) => setSupportTypeId(e.target.value)} placeholder="Any">
            {supportTypes.map((o) => (
              <option key={o.id} value={String(o.id)}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormControl>
        <Checkbox isChecked={isSequential} onChange={(e) => setIsSequential(e.target.checked)} mt={8}>
          Sequential workflow
        </Checkbox>
        <Checkbox isChecked={isActive} onChange={(e) => setIsActive(e.target.checked)} mt={8}>
          Active
        </Checkbox>
      </HStack>

      <Divider />

      <HStack justify="space-between">
        <Text fontWeight="700">Steps</Text>
        <Button size="sm" leftIcon={<Plus size={14} />} onClick={addStep}>
          Add step
        </Button>
      </HStack>

      {steps.map((step, stepIndex) => (
        <Box key={step.id ?? stepIndex} p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
          <HStack justify="space-between" mb={3}>
            <Text fontWeight="600">Step {stepIndex + 1}</Text>
            <HStack>
              <IconButton aria-label="Move up" size="xs" icon={<ChevronUp size={14} />} onClick={() => moveStep(stepIndex, -1)} />
              <IconButton aria-label="Move down" size="xs" icon={<ChevronDown size={14} />} onClick={() => moveStep(stepIndex, 1)} />
              <IconButton aria-label="Remove step" size="xs" colorScheme="red" variant="ghost" icon={<Trash2 size={14} />} onClick={() => removeStep(stepIndex)} />
            </HStack>
          </HStack>
          <VStack align="stretch" spacing={3}>
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={3} alignItems="start">
              <FormControl isRequired>
                <FormLabel fontSize="sm" mb={1}>
                  Title
                </FormLabel>
                <Input size="sm" value={step.title} onChange={(e) => updateStep(stepIndex, { title: e.target.value })} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm" mb={1}>
                  Status
                </FormLabel>
                <Select size="sm" value={step.status_code} onChange={(e) => updateStep(stepIndex, { status_code: e.target.value })}>
                  {getFilteredStatusOptions(step).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" mb={1}>
                  SLA (min)
                </FormLabel>
                <Input
                  size="sm"
                  type="number"
                  value={step.default_sla_minutes ?? ''}
                  onChange={(e) =>
                    updateStep(stepIndex, {
                      default_sla_minutes: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" mb={1}>
                  Depends on
                </FormLabel>
                <Select
                  size="sm"
                  value={step.depends_on_step_id ?? ''}
                  onChange={(e) => updateStep(stepIndex, { depends_on_step_id: e.target.value ? Number(e.target.value) : null })}
                >
                  <option value="">None</option>
                  {steps
                    .filter((candidate, candidateIndex) => candidateIndex < stepIndex && candidate.id)
                    .map((candidate, candidateIndex) => (
                      <option key={candidate.id ?? candidateIndex} value={candidate.id}>
                        Step {candidateIndex + 1}: {candidate.title || candidate.status_code}
                      </option>
                    ))}
                </Select>
              </FormControl>
            </SimpleGrid>
            <FormControl>
              <FormLabel fontSize="sm" mb={1}>
                Checklist for this step
              </FormLabel>
              <Select
                size="sm"
                value={step.checklist_template_id ?? ''}
                onChange={(e) =>
                  updateStep(stepIndex, {
                    checklist_template_id: e.target.value ? Number(e.target.value) : null,
                  })
                }
              >
                <option value="">No checklist for this step</option>
                {checklistLibrary.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                    {tpl.items_count != null ? ` (${tpl.items_count} items)` : ''}
                  </option>
                ))}
              </Select>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Seeds only onto the task created from this step. Other steps are not affected.
              </Text>
            </FormControl>
            <Text fontSize="xs" color="gray.500">
              {inferStepScope(step)
                ? `Status scoped to: ${inferStepScope(step) === 'fe_visit' ? 'FE Visit' : inferStepScope(step) === 'parts' ? 'Parts' : 'Permit'} + Generic`
                : 'Status: no scope filter applied'}
            </Text>
            <Text fontSize="xs" color="gray.600">
              Detected type:{' '}
              {(step.form_fields ?? []).some((f) => f.module === 'core.fe_schedule' || f.module === 'core.store')
                ? 'fe_schedule'
                : (step.form_fields ?? []).some((f) => f.module === 'core.parts')
                  ? 'parts'
                  : (step.form_fields ?? []).some((f) => f.module === 'core.work_permit')
                    ? 'work_permit'
                    : (step.form_fields ?? []).some((f) => f.module === 'core.assets')
                      ? 'assets'
                      : (step.action_type || 'none')}
            </Text>

            <Box>
              <Text fontSize="xs" fontWeight="700" mb={2}>
                Field bundles
              </Text>
              <HStack flexWrap="wrap" spacing={2} mb={2}>
                <Button size="xs" variant="outline" onClick={() => addBundle(stepIndex, 'fe')}>
                  + FE bundle
                </Button>
                <Button size="xs" variant="outline" onClick={() => addBundle(stepIndex, 'assets')}>
                  + Assets
                </Button>
                <Button size="xs" variant="outline" onClick={() => addBundle(stepIndex, 'parts')}>
                  + Parts
                </Button>
                <Button size="xs" variant="outline" onClick={() => addBundle(stepIndex, 'permit')}>
                  + Permit
                </Button>
              </HStack>
              <Text fontSize="xs" fontWeight="700" mb={2}>
                Add field
              </Text>
              <HStack flexWrap="wrap" spacing={2}>
                {Object.entries(groupedModules).map(([cat, mods]) =>
                  mods.map((m) => (
                    <Button
                      key={m.module_key}
                      size="xs"
                      variant="ghost"
                      onClick={() => addFieldToStep(stepIndex, m.module_key, m.label)}
                    >
                      + {m.label}
                    </Button>
                  ))
                )}
              </HStack>
            </Box>

            {(step.form_fields ?? []).length > 0 ? (
              <VStack align="stretch" spacing={2}>
                {(step.form_fields ?? []).map((field, fi) => (
                  <HStack key={field.key} p={2} bg="white" borderRadius="md" borderWidth="1px" align="center" spacing={3}>
                    <Badge flexShrink={0}>{field.module}</Badge>
                    <Input
                      size="sm"
                      flex="1"
                      minW="160px"
                      value={field.label}
                      onChange={(e) => {
                        const fields = [...(step.form_fields ?? [])]
                        fields[fi] = { ...fields[fi], label: e.target.value }
                        updateStep(stepIndex, { form_fields: fields })
                      }}
                    />
                    <Checkbox
                      size="sm"
                      isChecked={Boolean(field.required)}
                      onChange={(e) => {
                        const fields = [...(step.form_fields ?? [])]
                        fields[fi] = { ...fields[fi], required: e.target.checked }
                        updateStep(stepIndex, { form_fields: fields })
                      }}
                    >
                      Required
                    </Checkbox>
                    <IconButton
                      aria-label="Remove field"
                      size="xs"
                      variant="ghost"
                      icon={<Trash2 size={12} />}
                      onClick={() => {
                        updateStep(stepIndex, {
                          form_fields: (step.form_fields ?? []).filter((_, i) => i !== fi),
                        })
                      }}
                    />
                  </HStack>
                ))}
              </VStack>
            ) : (
              <Text fontSize="xs" color="gray.500">
                No form fields — add from palette above.
              </Text>
            )}
          </VStack>
        </Box>
      ))}

      <Divider />

      <FormControl>
        <FormLabel fontWeight="600">Standalone task checklist</FormLabel>
        <Select
          value={checklistTemplateId}
          onChange={(e) => setChecklistTemplateId(e.target.value)}
          placeholder="No checklist"
        >
          {checklistLibrary.map((tpl) => (
            <option key={tpl.id} value={tpl.id}>
              {tpl.name}
              {tpl.items_count != null ? ` (${tpl.items_count} items)` : ''}
            </option>
          ))}
        </Select>
        <Text fontSize="sm" color="gray.600" mt={2}>
          Used only when creating a single task from this template (Create task → From template). It is not applied to
          workflow steps — assign a checklist on each step instead when items differ by task.{' '}
          <Button as={RouterLink} to="/service-catalog/checklist-templates" variant="link" size="sm" colorScheme="blue">
            Manage checklist templates
          </Button>
        </Text>
      </FormControl>

      <HStack justify="flex-end" pt={4}>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button colorScheme="blue" onClick={() => void handleSubmit()} isLoading={saving}>
          {templateId ? 'Save template' : 'Create template'}
        </Button>
      </HStack>
    </VStack>
  )
}
